//
//
//  A set of validation rule methods for use with Vuetify Forms and Inputs.
//
//  Example Usages
//  --------------
//  Basic:
//    <x-text-field :rules="[ rules.exists ]" />
//    Error message: 'Required'
//
//  Use custom field name in error message:
//    <x-text-field :rules="[ v => rules.exists(v, 'Contract value' ]" />
//    Error message: 'Contract value is required'
//
//  Use custom error message:
//    <x-text-field :rules="[ v => rules.exists(v, false, 'A contract value must be supplied for the quote' ]" />
//    Error message: 'A contract value must be supplied for the quote'
//
//  Some rules require a parameter to compare against:
//    <x-text-field :rules="[ v => rules.greaterThan(v, 100) ]" />
//    Error message: 'Field should be greater than 100'
//
//

export default {
    methods: {
        ruleRequired(value, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('is required', fieldName, customMessage, 'Required');
            const isValid = this._required(value);
            return isValid || errorMessage;
        },
        ruleRequiredIf(value, requiredValue, requiredValueEquals, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('is required', fieldName, customMessage, 'Required');
            const isRequired = requiredValue === requiredValueEquals;
            const isValid = (!isRequired || this._required(value));
            return isValid || errorMessage;
        },
        ruleNumeric(value, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('should be numeric', fieldName, customMessage);
            const isValid = this._isEmpty(value) || ['number', 'string'].includes(typeof value) && !isNaN(value);
            return isValid || errorMessage;
        },
        ruleNumericString(value, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('should be numeric', fieldName, customMessage);
            const isValid = (/^([-+] ?)?\d{1,3}(,\d{3})*(\.\d+)?$/).test(value);
            return isValid || errorMessage;
        },
        ruleLessThan(value, comparisonValue, fieldName, customMessage) {
            // this rule should always be 'numeric'
            const numericResult = this.ruleNumeric(value, fieldName, customMessage);

            if (numericResult !== true) {
                return numericResult;
            }

            const errorMessage = this.getRuleErrorMessage(`should be less than ${comparisonValue}`, fieldName, customMessage);
            const isValid = this._isEmpty(value) || Number(value) < comparisonValue;
            return isValid || errorMessage;
        },
        ruleLessThanOrEqualTo(value, comparisonValue, fieldName, customMessage) {
            // this rule should always be 'numeric'
            const numericResult = this.ruleNumeric(value, fieldName, customMessage);

            if (numericResult !== true) {
                return numericResult;
            }

            const errorMessage = this.getRuleErrorMessage(`should be less than or equal to ${comparisonValue}`, fieldName, customMessage);
            const isValid = this._isEmpty(value) || Number(value) <= comparisonValue;
            return isValid || errorMessage;
        },
        ruleGreaterThan(value, comparisonValue, fieldName, customMessage) {
            // this rule should always be 'numeric'
            const numericResult = this.ruleNumeric(value, fieldName, customMessage);

            if (numericResult !== true) {
                return numericResult;
            }

            const errorMessage = this.getRuleErrorMessage(`should be greater than ${comparisonValue}`, fieldName, customMessage);
            const isValid = this._isEmpty(value) || Number(value) > comparisonValue;
            return isValid || errorMessage;
        },
        ruleGreaterThanOrEqualTo(value, comparisonValue, fieldName, customMessage) {
            // this rule should always be 'numeric'
            const numericResult = this.ruleNumeric(value, fieldName, customMessage);

            if (numericResult !== true) {
                return numericResult;
            }

            const errorMessage = this.getRuleErrorMessage(`should be greater than or equal to ${comparisonValue}`, fieldName, customMessage);
            const isValid = this._isEmpty(value) || Number(value) >= comparisonValue;
            return isValid || errorMessage;
        },
        ruleMinSelected(value, comparisonValue, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage(`should have at least ${comparisonValue} selected`, fieldName, customMessage);
            const isValid = this._isEmpty(value) || typeof value === 'object' && Array.isArray(value) && value.length >= comparisonValue;
            return isValid || errorMessage;
        },
        ruleAlphanumeric(value, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('should be alphanumeric', fieldName, customMessage);
            const alphanumericRegEx = /^[a-z0-9]*$/i;
            const isValid = this._isEmpty(value) || alphanumericRegEx.test(value);
            return isValid || errorMessage;
        },
        ruleUmr(value, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('is not a valid UMR format', fieldName, customMessage);
            const umrRegEx = /^b\d{4}\w{1,12}$/i;
            const isValid = this._isEmpty(value) || umrRegEx.test(value);
            return isValid || errorMessage;
        },
        _isEmpty(value) {
            // used to return true (value is valid) in all rules which are not explicitly required.
            return value === null || typeof value === 'undefined' || value === '';
        },
        _required(value) {
            return value !== null && typeof value !== 'undefined' && value !== '';
        },
        getRuleErrorMessage(appendedMessage, fieldName, customMessage, defaultMessage) {
            if (customMessage) {
                return customMessage;
            } else if (fieldName) {
                return `${fieldName} ${appendedMessage}`;
            } else if (defaultMessage) {
                return defaultMessage;
            }

            return `Field ${appendedMessage}`;
        },
        ruleEmailIsValid(email, fieldName, customMessage) {
            const errorMessage = this.getRuleErrorMessage('is invalid', fieldName, customMessage, 'Invalid');
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            const isValid = email ? re.test(String(email).toLowerCase()) : true;
            return isValid || errorMessage;
        }
    }
};
